
import * as THREE from "../../../libs/three.js/build/three.module.js";

// Feature B (PointView spec, Sprint 2) - the IFC/OBJ half of "Vis
// skæringslinjer mellem snitplanet og trekantsgeometrien": walks every
// triangle of a mesh's own geometry (in world space) and, for each one
// that the plane actually crosses, emits the one line segment where the
// plane cuts through it. Pure geometry, no Three.js scene/rendering
// concerns - SectionPlanesModule2D (index.html) turns the result into an
// actual THREE.LineSegments object and owns the per-plane cache.
//
// A single coplanar/degenerate triangle (all three vertices lying near-
// exactly in the plane) contributes nothing - vanishingly rare for real
// IFC/OBJ geometry, and not a meaningful "cut line" of its own even when
// it does happen.
const EPS = 1e-7;

// world-space AABB vs. plane - true if the box has any volume on both
// sides (or touches) the plane, i.e. this mesh is even worth walking
// triangle-by-triangle at all. A cheap, valuable pre-filter: most meshes
// in a large model don't cross a given plane.
export function boxIntersectsPlane(box, planePoint, planeNormal){
	// Project the box's extent onto the plane normal - the classic
	// "effective radius" test for an axis-aligned box against an arbitrary
	// plane.
	let center = new THREE.Vector3();
	box.getCenter(center);
	let extents = new THREE.Vector3();
	box.getSize(extents).multiplyScalar(0.5);

	let radius = extents.x * Math.abs(planeNormal.x)
		+ extents.y * Math.abs(planeNormal.y)
		+ extents.z * Math.abs(planeNormal.z);

	let dist = center.clone().sub(planePoint).dot(planeNormal);

	return Math.abs(dist) <= radius;
}

// Returns [{a: THREE.Vector3, b: THREE.Vector3}, ...] in world space - one
// segment per triangle the plane actually crosses. `geometry` is a
// THREE.BufferGeometry (indexed or not), `matrixWorld` the owning mesh's
// current world matrix.
export function intersectGeometryWithPlane(geometry, matrixWorld, planePoint, planeNormal){
	let segments = [];

	let posAttr = geometry.attributes.position;
	if(!posAttr){
		return segments;
	}
	let index = geometry.index;
	let triCount = index ? (index.count / 3) : (posAttr.count / 3);

	let v0 = new THREE.Vector3(), v1 = new THREE.Vector3(), v2 = new THREE.Vector3();
	let getVertex = (i, out) => {
		let vi = index ? index.getX(i) : i;
		out.fromBufferAttribute(posAttr, vi);
		out.applyMatrix4(matrixWorld);
		return out;
	};

	for(let t = 0; t < triCount; t++){
		getVertex(t * 3 + 0, v0);
		getVertex(t * 3 + 1, v1);
		getVertex(t * 3 + 2, v2);

		let d0 = v0.clone().sub(planePoint).dot(planeNormal);
		let d1 = v1.clone().sub(planePoint).dot(planeNormal);
		let d2 = v2.clone().sub(planePoint).dot(planeNormal);

		let crossings = [];
		let addCrossing = (da, db, pa, pb) => {
			if(Math.abs(da) < EPS && Math.abs(db) < EPS){
				return; // both on the plane - the coplanar-triangle case, skip
			}
			if((da > 0 && db > 0) || (da < 0 && db < 0)){
				return; // same side, no crossing on this edge
			}
			let t2 = da / (da - db);
			crossings.push(new THREE.Vector3().lerpVectors(pa, pb, t2));
		};

		addCrossing(d0, d1, v0, v1);
		addCrossing(d1, d2, v1, v2);
		addCrossing(d2, d0, v2, v0);

		if(crossings.length >= 2){
			// A perfectly plane-aligned edge can add a 3rd near-duplicate
			// crossing in rare cases - the first two are always the real
			// entry/exit points regardless.
			segments.push({a: crossings[0], b: crossings[1]});
		}
	}

	return segments;
}
